import {DisplayedDose, KeptDosesAlert} from '@/modules/patients/components/dialogs/kept-doses-alert'
import {Button, ButtonGroup, Text} from '@chakra-ui/react'
import {dialog, DialogBody, DialogFooter, DialogHeader} from '@cypro/ui/components'
import {DataList} from '@cypro/ui/components/ui/data-list'
import {multipleDigitsNumberFormatter} from '@cypro/ui/utils'
import {useTranslate} from '@tolgee/react'

const DIALOG_ID = 'confirm-characteristics-update'

interface Props {
  weight: {old: number | null | undefined; new: number}
  bsa: {old: number | null | undefined; new: number}
  recalculated: number
  kept: DisplayedDose[]
}

function formatValue(value: number | null | undefined) {
  return value ? multipleDigitsNumberFormatter.format(value) : '-'
}

function formatChange({old, new: next}: {old: number | null | undefined; new: number}) {
  return `${formatValue(old)} → ${formatValue(next)}`
}

function ConfirmCharacteristicsUpdateDialog({weight, bsa, recalculated, kept}: Props) {
  const {t} = useTranslate('common')

  return (
    <>
      <DialogHeader>{t('characteristics.update_confirm.title')}</DialogHeader>

      <DialogBody minHeight="none" spaceY="4">
        <Text>{t('characteristics.update_confirm.description')}</Text>
        <DataList
          items={[
            {label: t('characteristics.weight'), value: formatChange(weight)},
            {label: t('characteristics.bsa'), value: formatChange(bsa)},
            {label: t('characteristics.update_confirm.recalculated', 'Recalculated'), value: String(recalculated)},
          ]}
        />
        <KeptDosesAlert
          title={t('characteristics.update_confirm.dose_kept', 'Validated, dose not changed: {count}', {
            count: kept.length,
          })}
          doses={kept}
        />
      </DialogBody>

      <DialogFooter>
        <ButtonGroup size="md" colorPalette="cyan" width="100%" justifyContent="flex-end">
          <Button variant="outline" colorPalette="gray" onClick={() => dialog.close(DIALOG_ID, false)}>
            {t('cancel')}
          </Button>
          <Button onClick={() => dialog.close(DIALOG_ID, true)}>{t('confirm', 'Confirm')}</Button>
        </ButtonGroup>
      </DialogFooter>
    </>
  )
}

export async function confirmCharacteristicsUpdate(props: Props): Promise<boolean> {
  const confirmed = await dialog.open(DIALOG_ID, {children: <ConfirmCharacteristicsUpdateDialog {...props} />})
  return Boolean(confirmed)
}
